import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { categoryLabel } from "@/lib/blog";
import { Pencil, Trash2, Plus, LogOut, ExternalLink, Languages, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Post {
  id: string;
  title: string;
  slug: string;
  category: string;
  published: boolean;
  published_at: string | null;
  updated_at: string;
}

const AdminBlogList = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("id,title,slug,category,published,published_at,updated_at")
      .order("updated_at", { ascending: false });
    if (error) toast.error(error.message);
    setPosts(data ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const togglePublish = async (post: Post) => {
    const next = !post.published;
    const { error } = await supabase
      .from("blog_posts")
      .update({
        published: next,
        published_at: next ? (post.published_at ?? new Date().toISOString()) : post.published_at,
      })
      .eq("id", post.id);
    if (error) return toast.error(error.message);
    toast.success(next ? "Online" : "Offline");
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Beitrag gelöscht");
    load();
  };

  const [translating, setTranslating] = useState(false);

  const translateAll = async () => {
    setTranslating(true);
    let total = 0;
    try {
      for (let i = 0; i < 40; i++) {
        const { data, error } = await supabase.functions.invoke("translate-blog-posts", {
          body: { limit: 2 },
        });
        if (error) throw error;
        const processed = (data as { processed: number; remaining: number }).processed ?? 0;
        const remaining = (data as { processed: number; remaining: number }).remaining ?? 0;
        total += processed;
        if (processed > 0) toast.info(`Übersetzt: ${total} · offen: ${remaining}`);
        if (processed === 0 || remaining === 0) break;
      }
      toast.success(`Englische Übersetzungen aktualisiert (${total} Beiträge)`);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Übersetzung fehlgeschlagen");
    } finally {
      setTranslating(false);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-background border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Magazin verwalten</h1>
            <p className="text-xs text-muted-foreground">Praxis Innere Balance</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/blog" target="_blank"><ExternalLink className="w-4 h-4 mr-2" />Magazin ansehen</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">Beiträge</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" asChild>
              <Link to="/admin/mikrobi">Mikrobi-Auswertung</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/veranstaltungen">Veranstaltungen</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/blog/statistik"><BarChart3 className="w-4 h-4 mr-2" />Statistik</Link>
            </Button>
            <Button variant="outline" onClick={translateAll} disabled={translating}>
              <Languages className="w-4 h-4 mr-2" />
              {translating ? "Übersetze…" : "Fehlende EN-Übersetzungen"}
            </Button>
            <Button asChild>
              <Link to="/admin/blog/new"><Plus className="w-4 h-4 mr-2" />Neuer Beitrag</Link>
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Lade…</p>
        ) : posts.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">Noch keine Beiträge vorhanden.</p>
            <Button asChild><Link to="/admin/blog/new">Ersten Beitrag erstellen</Link></Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <Card key={post.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-medium text-foreground truncate">{post.title}</h3>
                    <Badge variant={post.published ? "default" : "secondary"} className="text-xs">
                      {post.published ? "Online" : "Entwurf"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {categoryLabel(post.category)} · /{post.slug}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={post.published} onCheckedChange={() => togglePublish(post)} />
                    <span className="text-xs text-muted-foreground hidden sm:inline">Online</span>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/admin/blog/${post.id}`}><Pencil className="w-4 h-4" /></Link>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm"><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Beitrag löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          „{post.title}" wird unwiderruflich gelöscht.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(post.id)}>Löschen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlogList;
